import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'

import App from './App.vue'
import router from './router'

// Import global styles
import './styles/global.css'

// Naive UI 样式会自动导入，无需手动导入

// Import language files
import en from './locales/en.json'
import zh from './locales/zh.json'

// Import types
import './types/vue-i18n.d.ts'
import './types/global.d.ts'
import './types/env.d.ts'


import './types/css.d.ts'

// Import mobile optimizations (currently disabled)
// import { initMobileOptimizations } from './utils/mobile'

// Import i18n utilities
import { initializeI18n, getInitialLocale } from './utils/i18n-simple'

// Import theme utilities
import { initializeTheme } from './utils/theme'

// Import error handling
import { errorHandler, ConsoleEnhancer, PerformanceMonitor } from './utils/error-handler'
// import ErrorBoundaryPlugin from './plugins/error-boundary'
import devTools from './utils/dev-tools'

const i18n = createI18n({
  legacy: false,
  locale: getInitialLocale(), // 使用智能语言检测
  fallbackLocale: 'en',
  messages: {
    en,
    zh
  }
})

// 初始化错误处理和控制台增强
if (import.meta.env.DEV) {
  ConsoleEnhancer.enhance()
  PerformanceMonitor.mark('app-init')
}

const app = createApp(App)

// 设置 Vue 错误处理器
app.config.errorHandler = (err: unknown, instance, info: string) => {
  const error = err instanceof Error ? err : new Error(String(err))
  errorHandler.vueErrorHandler(error, instance, info)
}

app.use(createPinia())
app.use(router)
app.use(i18n)
// app.use(ErrorBoundaryPlugin, {
//   fallback: 'Something went wrong. Please try again.',
//   onError: (error: unknown, instance: unknown, info: string) => {
//     // 自定义错误处理逻辑
//     if (import.meta.env.DEV) {
//       console.group('🚨 Vue Error Boundary')
//       console.error('Error:', error)
//       console.log('Component:', (instance as any)?.$options?.name || 'Unknown')
//       console.log('Info:', info)
//       console.groupEnd()
//     }
//   }
// })

// 初始化国际化
initializeI18n(i18n)

// 初始化主题系统
const cleanupTheme = initializeTheme()

// 临时禁用移动端优化来调试滚动问题
// initMobileOptimizations()

app.mount('#app')

// 开发环境下测量应用初始化时间和初始化开发工具
if (import.meta.env.DEV) {
  PerformanceMonitor.measure('app-init')
  
  // 初始化开发工具
  devTools
  
  // 添加全局引用以便调试
  interface WindowWithDebug extends Window {
    __VUE_APP__?: typeof app
    __VUE_VERSION__?: string
  }
  
  const windowWithDebug = window as WindowWithDebug
  windowWithDebug.__VUE_APP__ = app
  windowWithDebug.__VUE_VERSION__ = app.version
}

// 应用卸载时清理
window.addEventListener('beforeunload', () => {
  cleanupTheme()
})

// 注册 Service Worker
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);

        // 检查更新
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // 有新版本可用
                console.log('New content is available; please refresh.');

                // 可以在这里显示更新提示
                if (confirm('New version available! Refresh to update?')) {
                  newWorker.postMessage({ type: 'SKIP_WAITING' });
                  window.location.reload();
                }
              }
            });
          }
        });
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });

    // 监听 Service Worker 控制器变化
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });
  });
}

// 移动端性能优化
if ('requestIdleCallback' in window) {
  // 在浏览器空闲时预加载资源
  requestIdleCallback(() => {
    // 暂时注释掉字体预加载
    // const fontLink = document.createElement('link');
    // fontLink.rel = 'preload';
    // fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
    // fontLink.as = 'style';
    // document.head.appendChild(fontLink);
  });
}

// 移除可能阻止滚动的代码，保持页面正常滚动功能
